import { inject, injectable } from 'inversify';
import type { ViewController } from '../../View.controller';
import type { IReportLifecycle } from './interfaces';
import { TYPES } from '@/engine/ioc/types';
import type { ViewsService } from '../../Views.service';
import { REPORT_VIEW_DESCRIPTOR } from './constants';
import type { ThemeStore } from '@/assets/branding/Theme.store';
import type { ThemeName } from '@/assets/branding/interfaces.ts';

@injectable()
/**
 * Represents the lifecycle of a report.
 */
export class ReportLifecycle implements IReportLifecycle {
  private _viewController: ViewController;

  private _viewsService: ViewsService;

  private _themeStore: ThemeStore;

  private _originalViewDescriptor?: string;

  private _originalTheme?: ThemeName;

  public constructor(
    @inject(TYPES.ViewController) viewController: ViewController,
    @inject(TYPES.ViewsService) viewsService: ViewsService,
    @inject(TYPES.ThemeStore) themeStore: ThemeStore
  ) {
    this._viewController = viewController;
    this._viewsService = viewsService;
    this._themeStore = themeStore;
  }

  /**
   * Called before creating a report.
   * Prepares the environment for report creation.
   * @returns A promise that resolves when the preparation is complete.
   */
  public onBeforeCreateReport(): Promise<void> {
    this._saveOriginalViewDescriptor();
    this._saveCurrentTheme();
    this._setReportTheme();
    return Promise.resolve();
  }

  /**
   * Called after creating a report.
   * @returns A promise that resolves when the resetting is complete.
   */
  public onAfterCreateReport(): Promise<void> {
    this._restoreViewPortSizes();
    this._removeReportView();
    this._restoreOriginalView();
    this._restoreTheme();
    return Promise.resolve();
  }

  private _saveOriginalViewDescriptor(): void {
    this._originalViewDescriptor = this._viewsService.currentView?.getDescriptor();
  }

  private _setReportTheme(): void {
    this._themeStore.setTheme('report');
  }

  private _saveCurrentTheme(): void {
    this._originalTheme = this._themeStore.theme;
  }

  private _restoreTheme(): void {
    if (this._originalTheme) {
      this._themeStore.setTheme(this._originalTheme);
    }
  }

  private _restoreViewPortSizes(): void {
    const viewPorts = this._viewController.getViewPorts();
    for (const viewPort of viewPorts) {
      this._viewController.getViewPortController(viewPort.descriptor)?.resetSize();
    }
  }

  private _removeReportView(): void {
    const reportView = this._viewsService.getViewByDescriptor(REPORT_VIEW_DESCRIPTOR);
    if (reportView) {
      this._viewsService.removeView(reportView);
    }
  }

  private _restoreOriginalView(): void {
    if (this._originalViewDescriptor) {
      const originalView = this._viewsService.getViewByDescriptor(this._originalViewDescriptor);
      if (originalView) {
        this._viewsService.setCurrentView(originalView);
      }
    }
  }
}
